import React, { useState } from "react";
import { Timer as TimerIcon, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAlerts } from "@/context/AlertsContext";
import { useApp } from "@/context/AppContext";

const PRESETS = [5, 15, 25, 45];

export const TimerDialog = ({ open, onOpenChange, tasks = [] }) => {
  const { t } = useApp();
  const { startTimer } = useAlerts();
  const [minutes, setMinutes] = useState(25);
  const [taskLabel, setTaskLabel] = useState("");

  const begin = () => {
    const mins = Math.max(1, Math.min(180, parseInt(minutes, 10) || 25));
    startTimer(mins * 60, taskLabel.trim());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="timer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <TimerIcon className="w-5 h-5 text-primary" /> {t("timer")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("timer")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                data-testid={`timer-preset-${p}`}
                onClick={() => setMinutes(p)}
                className={`h-12 rounded-xl font-semibold transition-all ${
                  Number(minutes) === p
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-foreground hover:bg-accent"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <input
              type="number"
              min="1"
              max="180"
              data-testid="timer-minutes-input"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              className="w-24 h-11 bg-background border border-border rounded-xl px-3 text-center outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
            <span className="text-sm text-muted-foreground">{t("minutes")}</span>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              {t("forTask")}
            </label>
            <select
              data-testid="timer-task-select"
              value={taskLabel}
              onChange={(e) => setTaskLabel(e.target.value)}
              className="mt-2 w-full h-11 bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            >
              <option value="">{t("focusLabel")}</option>
              {tasks
                .filter((tk) => !tk.completed)
                .map((tk) => (
                  <option key={tk.id} value={tk.title}>
                    {tk.title}
                  </option>
                ))}
            </select>
          </div>

          <button
            data-testid="timer-start-btn"
            onClick={begin}
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all"
          >
            <Play className="w-5 h-5" /> {t("start")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
